import { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Drawer,
  DrawerCloseButton,
  DrawerOverlay,
  DrawerContent,
  DrawerFooter,
  DrawerBody,
  DrawerHeader,
  VStack,
  Text,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Image,
  Icon,
} from '@chakra-ui/react';

import CouponItem from './CouponItem';

import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import couponAtom from '~/recoil/atom/coupon';
import { useRecoilValue } from 'recoil';

import { useNavigate, useParams } from 'react-router-dom';

import * as fp from 'lodash/fp';

import updateCart from '~/firebase/update/updateCart';

import useCartId from '~/hooks/useCartId';

import { IoArrowForwardCircle } from 'react-icons/io5';

import { Coupon } from '~/types/coupon';

import couponImg from '~/assets/images/coupon.png';
import { PLATFORM_ID } from '~/globalVariables';

type CouponsModalProps = {
  displayAvailableCoupons: Array<Coupon & { storesName: Array<string> }>;
  displayNotAvailableCoupons: Array<Coupon & { storesName: Array<string> }>;
  notDisplayAvailableCoupons: Array<Coupon & { storesName: Array<string> }>;
  notDisplayNotAvailableCoupons: Array<Coupon & { storesName: Array<string> }>;
  isOpen: boolean;
  onClose: () => void;
};

export default function CouponsModal({
  displayAvailableCoupons,
  displayNotAvailableCoupons,
  notDisplayAvailableCoupons,
  notDisplayNotAvailableCoupons,
  isOpen,
  onClose,
}: CouponsModalProps) {
  const navigate = useNavigate();
  const { groupId = '' } = useParams();

  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const coupon = useRecoilValue(couponAtom);
  const { cartId } = useCartId();

  const [searchCode, setSearchCode] = useState<string>('');

  const [inputText, setInputText] = useState<string>('');

  const [couponId, setCouponId] = useState<string>(coupon?.id || '');

  useEffect(() => {
    if (isOpen) {
      setCouponId(coupon?.id || '');
    }
  }, [isOpen]);

  const filteredNotDisplayAvailableCoupons = useMemo(() => {
    if (searchCode) {
      return notDisplayAvailableCoupons.filter(fp.propEq('code', searchCode));
    }
    return [];
  }, [searchCode, notDisplayAvailableCoupons]);

  const handleChangeCouponId = (id: string) => {
    setCouponId((prev) => (fp.eq(prev, id) ? '' : id));
  };

  const handleUpdateCoupon = async () => {
    const selectCoupon = displayAvailableCoupons
      .concat(notDisplayAvailableCoupons)
      .find(fp.propEq('id', couponId));
    if (!selectCoupon) {
      try {
        console.log('clear coupon');
        await updateCart(PLATFORM_ID, cartId, {
          campaignId: '',
          coupon: null,
        });
        onClose();
      } catch (err) {}
      return;
    }

    try {
      await updateCart(PLATFORM_ID, cartId, {
        campaignId: selectCoupon.id,
        coupon: selectCoupon,
      });
      onClose();
    } catch (err) {
      console.error('send', err);
    }
  };

  if (isLargerThan768) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader textAlign="center" fontSize="2xl" mt={4} borderBottomWidth="1px">
            優惠券
          </ModalHeader>
          <ModalBody>
            {fp.cond([
              [
                fp.where<
                  Partial<{
                    searchCode: string;
                    displayCouponAmount: number;
                    notDisplayCouponAmount: number;
                  }>
                >({
                  displayCouponAmount: fp.gt(fp.__, 0),
                  searchCode: fp.eq(''),
                }),
                fp.always(
                  <VStack align="stretch" spacing={6}>
                    <Text fontSize="xs">
                      以下為此購物車符合標準可使用的優惠卷，想要看更多優惠卷可到「專屬優惠」。注意：優惠卷無法合併使用。
                    </Text>
                    <VStack align="stretch">
                      <Flex align="center">
                        <Box flex="1" h="1px" bgColor="gray.300" />
                        <Text px={1} fontWeight="bold" fontSize="sm">
                          可使用的優惠卷
                        </Text>
                        <Box flex="1" h="1px" bgColor="gray.300" />
                      </Flex>
                      {fp.gt(displayAvailableCoupons.length, 0) ? (
                        displayAvailableCoupons.map((coupon) => (
                          <CouponItem
                            key={coupon.id}
                            coupon={coupon}
                            onNavigate={() =>
                              navigate(
                                groupId
                                  ? `/group/${groupId}/coupons/${coupon.id}`
                                  : `/coupons/${coupon.id}`,
                              )
                            }
                            isSelected={fp.eq(coupon.id, couponId)}
                            onClick={() => handleChangeCouponId(coupon.id)}
                          />
                        ))
                      ) : (
                        <VStack direction="column" align="center" py={4} spacing={4}>
                          <Text color="gray.500" fontSize="sm">
                            目前沒有達到門檻可用的優惠券
                          </Text>
                          <Button
                            size="lg"
                            rightIcon={<Icon as={IoArrowForwardCircle} boxSize="1.5rem" />}
                            onClick={() => navigate('/')}
                          >
                            點餐去
                          </Button>
                        </VStack>
                      )}
                    </VStack>
                    <VStack align="stretch">
                      <Flex align="center">
                        <Box flex="1" h="1px" bgColor="gray.300" />
                        <Text px={1} fontWeight="bold" fontSize="sm">
                          未達門檻的優惠卷
                        </Text>
                        <Box flex="1" h="1px" bgColor="gray.300" />
                      </Flex>
                      {displayNotAvailableCoupons.map((coupon) => (
                        <CouponItem
                          isEnable={false}
                          isSelected={fp.eq(coupon.id, couponId)}
                          key={coupon.id}
                          coupon={coupon}
                          onNavigate={() =>
                            navigate(
                              groupId
                                ? `/group/${groupId}/coupons/${coupon.id}`
                                : `/coupons/${coupon.id}`,
                            )
                          }
                        />
                      ))}
                    </VStack>
                  </VStack>,
                ),
              ],
              [
                fp.where<
                  Partial<{
                    searchCode: string;
                    displayCouponAmount: number;
                    notDisplayCouponAmount: number;
                  }>
                >({
                  notDisplayCouponAmount: fp.gt(fp.__, 0),
                }),
                fp.always(
                  <VStack>
                    {filteredNotDisplayAvailableCoupons.map((coupon) => (
                      <CouponItem
                        isSelected={fp.eq(coupon.id, couponId)}
                        onClick={() => handleChangeCouponId(coupon.id)}
                        key={coupon.id}
                        coupon={coupon}
                        onNavigate={() =>
                          navigate(
                            groupId
                              ? `/group/${groupId}/coupons/${coupon.id}`
                              : `/coupons/${coupon.id}`,
                          )
                        }
                      />
                    ))}
                  </VStack>,
                ),
              ],
              [
                fp.T,
                fp.always(
                  <Flex direction="column" my={10} maxW="300px" mx="auto">
                    <Image src={couponImg} />
                    <Text color="gray.500" textAlign="center">
                      目前沒有公開可使用的優惠卷
                    </Text>
                  </Flex>,
                ),
              ],
            ])({
              searchCode: searchCode,
              displayCouponAmount:
                displayAvailableCoupons.length + displayNotAvailableCoupons.length,
              notDisplayCouponAmount: filteredNotDisplayAvailableCoupons.length,
            })}
          </ModalBody>
          <ModalFooter p={0}>
            <Flex w="full" direction="column">
              <FormControl p={4}>
                <FormLabel>輸入優惠碼</FormLabel>
                <InputGroup>
                  <Input
                    placeholder="請輸入優惠碼"
                    value={inputText}
                    onChange={(e) => {
                      setInputText(e.target.value);
                      if (!e.target.value) setSearchCode('');
                    }}
                  />
                  <InputRightAddon
                    cursor="pointer"
                    onClick={() => setSearchCode(inputText)}
                    bgColor={!!inputText ? 'dark' : 'gray.200'}
                    color={!!inputText ? 'white' : 'gray.500'}
                  >
                    輸入
                  </InputRightAddon>
                </InputGroup>
              </FormControl>
              <Button isFullWidth size="lg" borderRadius="none" onClick={handleUpdateCoupon}>
                確定
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader textAlign="center" fontSize="2xl" mt={4} borderBottomWidth="1px">
          優惠券
        </DrawerHeader>
        <DrawerBody>
          {fp.cond([
            [
              fp.where<
                Partial<{
                  searchCode: string;
                  displayCouponAmount: number;
                  notDisplayCouponAmount: number;
                }>
              >({
                displayCouponAmount: fp.gt(fp.__, 0),
                searchCode: fp.eq(''),
              }),
              fp.always(
                <VStack align="stretch" spacing={6}>
                  <Text fontSize="xs">
                    以下為此購物車符合標準可使用的優惠卷，想要看更多優惠卷可到「專屬優惠」。注意：優惠卷無法合併使用。
                  </Text>
                  <VStack align="stretch">
                    <Flex align="center">
                      <Box flex="1" h="1px" bgColor="gray.300" />
                      <Text px={1} fontWeight="bold" fontSize="sm">
                        可使用的優惠卷
                      </Text>
                      <Box flex="1" h="1px" bgColor="gray.300" />
                    </Flex>
                    {fp.gt(displayAvailableCoupons.length, 0) ? (
                      displayAvailableCoupons.map((coupon) => (
                        <CouponItem
                          key={coupon.id}
                          coupon={coupon}
                          isSelected={fp.eq(coupon.id, couponId)}
                          onClick={() => handleChangeCouponId(coupon.id)}
                          onNavigate={() =>
                            navigate(
                              groupId
                                ? `/group/${groupId}/coupons/${coupon.id}`
                                : `/coupons/${coupon.id}`,
                            )
                          }
                        />
                      ))
                    ) : (
                      <VStack direction="column" align="center" py={4} spacing={4}>
                        <Text color="gray.500" fontSize="sm">
                          目前沒有達到門檻可用的優惠券
                        </Text>
                        <Button
                          size="lg"
                          rightIcon={<Icon as={IoArrowForwardCircle} boxSize="1.5rem" />}
                          onClick={() => navigate('/')}
                        >
                          點餐去
                        </Button>
                      </VStack>
                    )}
                  </VStack>
                  <VStack align="stretch">
                    <Flex align="center">
                      <Box flex="1" h="1px" bgColor="gray.300" />
                      <Text px={1} fontWeight="bold" fontSize="sm">
                        未達門檻的優惠卷
                      </Text>
                      <Box flex="1" h="1px" bgColor="gray.300" />
                    </Flex>
                    {displayNotAvailableCoupons.map((coupon) => (
                      <CouponItem
                        isEnable={false}
                        isSelected={fp.eq(coupon.id, couponId)}
                        key={coupon.id}
                        coupon={coupon}
                        onNavigate={() =>
                          navigate(
                            groupId
                              ? `/group/${groupId}/coupons/${coupon.id}`
                              : `/coupons/${coupon.id}`,
                          )
                        }
                      />
                    ))}
                  </VStack>
                </VStack>,
              ),
            ],
            [
              fp.where<
                Partial<{
                  searchCode: string;
                  displayCouponAmount: number;
                  notDisplayCouponAmount: number;
                }>
              >({
                notDisplayCouponAmount: fp.gt(fp.__, 0),
              }),
              fp.always(
                <VStack>
                  {filteredNotDisplayAvailableCoupons.map((coupon) => (
                    <CouponItem
                      isSelected={fp.eq(coupon.id, couponId)}
                      onClick={() => handleChangeCouponId(coupon.id)}
                      key={coupon.id}
                      coupon={coupon}
                      onNavigate={() =>
                        navigate(
                          groupId
                            ? `/group/${groupId}/coupons/${coupon.id}`
                            : `/coupons/${coupon.id}`,
                        )
                      }
                    />
                  ))}
                </VStack>,
              ),
            ],
            [
              fp.T,
              fp.always(
                <Flex direction="column" my={10} maxW="300px" mx="auto">
                  <Image src={couponImg} />
                  <Text color="gray.500" textAlign="center">
                    目前沒有公開可使用的優惠卷
                  </Text>
                </Flex>,
              ),
            ],
          ])({
            searchCode: searchCode,
            displayCouponAmount: displayAvailableCoupons.length + displayNotAvailableCoupons.length,
            notDisplayCouponAmount: filteredNotDisplayAvailableCoupons.length,
          })}
        </DrawerBody>
        <DrawerFooter p={0} position="sticky" bottom={0} bgColor="white">
          <Flex w="full" direction="column">
            <FormControl p={4}>
              <FormLabel>輸入優惠碼</FormLabel>
              <InputGroup>
                <Input
                  placeholder="請輸入優惠碼"
                  value={inputText}
                  onChange={(e) => {
                    setInputText(e.target.value);
                    if (!e.target.value) setSearchCode('');
                  }}
                />
                <InputRightAddon
                  cursor="pointer"
                  onClick={() => setSearchCode(inputText)}
                  bgColor={!!inputText ? 'dark' : 'gray.200'}
                  color={!!inputText ? 'white' : 'gray.500'}
                >
                  輸入
                </InputRightAddon>
              </InputGroup>
            </FormControl>
            <Button isFullWidth size="lg" borderRadius="none" onClick={handleUpdateCoupon}>
              確定
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
